// Wire-format convention: dates flow end-to-end as ISO `YYYY-MM-DD` strings, not Date objects.
// No `Serialized<T>` wrapper, no JSON reviver — domain types use `string` for dates throughout.
// Callers that need a `Date` use `isoToDate` from `./date`. Do not add a reviver here.
import { ApiError, type ApiErrorBody } from './ApiError';
import { getApiBaseUrl } from './config';
import { get as getFromStorage } from './storage';

const SETTINGS_STORAGE_KEY = 'nuxni-time-tracker:settings';

type SettingsLike = { pat?: string };

const readPat = (): string | null => {
    const settings = getFromStorage<SettingsLike>(SETTINGS_STORAGE_KEY);
    const pat = settings?.pat;
    if (!pat || pat.trim() === '') {
        return null;
    }
    return pat.trim();
};

const buildAuthHeader = (): string | null => {
    const pat = readPat();
    if (!pat) {
        return null;
    }
    return `Basic ${btoa(`:${pat}`)}`;
};

const buildUrl = (path: string, query?: Record<string, string | number | undefined>): string => {
    const apiBaseUrl = getApiBaseUrl();
    const base = apiBaseUrl ? `${apiBaseUrl}${path}` : path;
    if (!query) {
        return base;
    }
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
        if (value === undefined) {
            continue;
        }
        search.set(key, String(value));
    }
    const qs = search.toString();
    return qs ? `${base}?${qs}` : base;
};

const parseErrorBody = async (response: Response): Promise<ApiErrorBody | null> => {
    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.includes('application/json')) {
        return null;
    }
    try {
        return (await response.json()) as ApiErrorBody;
    } catch {
        return null;
    }
};

const errorMessage = (status: number, body: ApiErrorBody | null): string => {
    if (body?.message) {
        return body.message;
    }
    if (body?.code) {
        return body.code;
    }
    return `HTTP ${status}`;
};

export type RequestInit = {
    method: 'GET' | 'PUT' | 'POST' | 'DELETE';
    path: string;
    query?: Record<string, string | number | undefined>;
    body?: unknown;
    ifMatch?: string;
};

export type RequestResult<T> = {
    data: T;
    headers: Headers;
};

export const request = async <T>(init: RequestInit): Promise<RequestResult<T>> => {
    const auth = buildAuthHeader();
    if (!auth) {
        throw new ApiError(401, 'PAT not configured. Set it in Settings.', null);
    }

    const headers: Record<string, string> = {
        Authorization: auth,
        Accept: 'application/json',
    };
    if (init.ifMatch) {
        headers['If-Match'] = init.ifMatch;
    }
    if (init.body !== undefined) {
        headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(buildUrl(init.path, init.query), {
        method: init.method,
        headers,
        body: init.body === undefined ? undefined : JSON.stringify(init.body),
    });

    if (!response.ok) {
        const body = await parseErrorBody(response);
        throw new ApiError(response.status, errorMessage(response.status, body), body);
    }

    if (response.status === 204) {
        return { data: undefined as T, headers: response.headers };
    }

    const text = await response.text();
    const data = (text ? JSON.parse(text) : undefined) as T;
    return { data, headers: response.headers };
};

export const requestJson = async <T>(init: RequestInit): Promise<T> => {
    const { data } = await request<T>(init);
    return data;
};

export type BlobResult = {
    blob: Blob;
    /** Parsed filename from `Content-Disposition: attachment; filename="…"`, when present. */
    filename: string | null;
};

const parseFilenameFromDisposition = (raw: string | null): string | null => {
    if (!raw) {
        return null;
    }
    const utf8 = raw.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8) {
        try {
            return decodeURIComponent(utf8[1]);
        } catch {
            // fall through to the plain filename
        }
    }
    const plain = raw.match(/filename="?([^";]+)"?/i);
    return plain ? plain[1] : null;
};

export const requestBlob = async (init: Omit<RequestInit, 'body'>): Promise<BlobResult> => {
    const auth = buildAuthHeader();
    if (!auth) {
        throw new ApiError(401, 'PAT not configured. Set it in Settings.', null);
    }

    const headers: Record<string, string> = { Authorization: auth };
    if (init.ifMatch) {
        headers['If-Match'] = init.ifMatch;
    }

    const response = await fetch(buildUrl(init.path, init.query), {
        method: init.method,
        headers,
    });

    if (!response.ok) {
        const body = await parseErrorBody(response);
        throw new ApiError(response.status, errorMessage(response.status, body), body);
    }

    const blob = await response.blob();
    const filename = parseFilenameFromDisposition(response.headers.get('content-disposition'));
    return { blob, filename };
};
